import React, {useEffect, useRef, useState} from 'react'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import Breadcrumb, {BreadcrumbType} from '../../components/Breadcrumb'
import {
  AssetsBox,
  AssetTitle,
  IconsBox,
  LeftBox,
  LoggedOutBox,
  ObjectiveText,
  PlayerBox,
  RightBox,
  TimeBox,
  TranscriptBox,
  TranscriptText,
  TranscriptTitle,
} from '../VideoPage/styles'
import {Link as RouterLink} from 'react-router-dom'
import ClockIcon from '../../../images/icons/new-dashboard/clock-icon.svg'
import {getDivisionDetails} from '../../../constants/divisions'
import Cookies from 'universal-cookie'
import {useSelector} from 'react-redux'
import Loading from '../../../components/Loading'
import {LoadingStatuses} from '../../../components/types/LoadingStatusses'
import convertDuration from '../../../utils/convertDuration'
import {useAppDispatch} from '../../../redux/configureStore'
import {selectMember} from '../../../redux/members'
import {useAuth} from '../../../components/AuthProvider'
import LockIcon from '@mui/icons-material/Lock'
import {useWindowDimensions} from '../../../hooks/useWindowDimensions'
import {LessonPlan} from '../../../redux/lessons'
import {Course, selectCourseStatus} from '../../../redux/courses'
import {
  selectVideoStatus,
  VideoGroup,
  Video as VideoModel,
} from '../../../redux/videos'
import LessonPlanSidenav from '../../components/LessonPlanSidenav'
import VideoEnded from '../../../components/VideoEnded'
import {handleUpgrade} from '../../../utils/upgradeHelper'
import renderHTML from '../../../utils/renderHTML'

type Props = {
  videoId: string
  video?: VideoModel
  course?: Course
  videoEnded: boolean
  onUpdate: (value: number, playerDuration?: number) => void
  onChangeVideoEnded: (value: boolean) => void
  lesson?: LessonPlan
  hasAccess?: boolean
  nextGroup?: VideoGroup
  currentGroup?: VideoGroup
  currentIndex?: number
}

const View = ({
  videoId,
  video,
  course,
  onUpdate,
  videoEnded,
  onChangeVideoEnded,
  lesson,
  hasAccess,
  nextGroup,
  currentGroup,
  currentIndex,
}: Props) => {
  const dispatch = useAppDispatch()
  const auth = useAuth()
  const {isMobile} = useWindowDimensions()

  const [isExpanded, setIsExpanded] = useState(isMobile ? false : true)

  const cookies = new Cookies()

  const breadcrumbs: BreadcrumbType[] = [
    {
      title: course?.title ?? '',
      url: `/courses/${course?.id}`,
    },
    {
      title: video?.title ?? '',
      url: `#`,
    },
  ]

  const member = useSelector(selectMember)
  const courseStatus = useSelector(selectCourseStatus)
  const videoStatus = useSelector(selectVideoStatus)
  let lengthUpdated = false

  const playerRef: any = useRef()

  let nextEvent = 10

  const onSuccess = (player: any) => {
    const myPlayer = player.ref
    const videoDuration = convertDuration(video?.duration ?? '0')

    //EVENT THROWN WHEN THE VIDEO ENDS
    myPlayer.on('ended', function () {
      const lines = document.querySelectorAll('#transcript .line')
      lines.forEach(function (line) {
        line.classList.remove('transcriptActive')
      })

      onChangeVideoEnded(true)
    })

    // EVENT THROWN WHEN THE VIDEO STARTS
    myPlayer.on('loadstart', function () {
      myPlayer.currentTime(0)

      myPlayer.playbackRate(Number(cookies.get('bksPlaybackRate')) || 1)

      const savedVolume = cookies.get('bksVolume')
      const savedMuted = cookies.get('bksMuted')
      if (savedVolume !== undefined) {
        myPlayer.volume(Number(savedVolume))
      }
      if (savedMuted !== undefined) {
        myPlayer.muted(savedMuted === 'true')
      }
    })

    const saveVolumeAndMute = () => {
      cookies.set('bksVolume', myPlayer.volume(), {path: '/'})
      cookies.set('bksMuted', myPlayer.muted(), {path: '/'})
    }

    // EVENT THROWN WHEN THE VOLUME CHANGES
    myPlayer.on('volumechange', saveVolumeAndMute)
    myPlayer.on('muted', saveVolumeAndMute)
    myPlayer.on('unmuted', saveVolumeAndMute)

    //EVENT THROWN EVERY SECOND WHILE THE VIDEO IS PLAYING
    myPlayer.on('timeupdate', function () {
      const time = myPlayer.currentTime()
      const playerDuration = Math.floor(myPlayer.duration())

      const link = document.querySelector(
        '#transcript .line[data-time="' + Math.round(time) + '"]',
      )
      if (link) {
        const lines = document.querySelectorAll('#transcript .line')
        lines.forEach(function (line) {
          line.classList.remove('transcriptActive')
        })

        link.classList.add('transcriptActive')
      }

      const percent = Math.round((time / playerDuration) * 100)
      if (percent % 10 == 0) {
        if (percent >= nextEvent) {
          updateProgress(
            percent,
            playerDuration !== videoDuration && !lengthUpdated
              ? playerDuration
              : undefined,
          )
          lengthUpdated = true
        }
      }

      const playbackRate = myPlayer.playbackRate()
      if (playbackRate !== cookies.get('bksPlaybackRate')) {
        cookies.set('bksPlaybackRate', playbackRate, {
          path: '/',
        })
      }
    })

    let initialSetupDone = false

    const setCaptions = (enabled: any) => {
      const tracks = myPlayer.textTracks()
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].kind === 'captions' || tracks[i].kind === 'subtitles') {
          tracks[i].mode = enabled ? 'showing' : 'hidden'
        } else {
          tracks[i].mode = 'disabled'
        }
      }
    }

    myPlayer.on('loadedmetadata', () => {
      const captionsEnabled = cookies.get('captionsEnabled') === 'true'
      setCaptions(captionsEnabled)
      initialSetupDone = true
    })

    myPlayer.textTracks().addEventListener('change', () => {
      if (initialSetupDone) {
        const tracks = myPlayer.textTracks()
        let captionsEnabled = false
        for (let i = 0; i < tracks.length; i++) {
          if (tracks[i].kind === 'captions' || tracks[i].kind === 'subtitles') {
            if (tracks[i].mode === 'showing') {
              captionsEnabled = true
              break
            }
          }
        }
        cookies.set('captionsEnabled', String(captionsEnabled), {
          path: '/',
        })
      }
    })
  }

  useEffect(() => {
    const transcriptLines = document.querySelectorAll('#transcript .line')

    transcriptLines.forEach(line => {
      line.addEventListener('click', () => {
        transcriptLines.forEach(line => {
          line.classList.remove('transcriptActive')
        })

        line.classList.add('transcriptActive')

        playerRef.current?.player.currentTime(
          Number(line.getAttribute('data-time')),
        )
      })
    })
  }, [video])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'c') {
        e.preventDefault()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const updateProgress = (percent: number, playerDuration?: number) => {
    nextEvent = percent + 10
    onUpdate(percent, playerDuration)
  }

  const division = getDivisionDetails(course?.abbreviation ?? null)

  return (
    <>
      {!isMobile && <Breadcrumb breadcrumbs={breadcrumbs} />}
      <AssetsBox>
        <LeftBox $isExpanded={isExpanded}>
          <LessonPlanSidenav
            id={videoId}
            course={course!}
            currentGroup={currentGroup}
            isExpanded={isExpanded}
            onChangeIsExpanded={(value: boolean) => setIsExpanded(value)}
          />
        </LeftBox>
        <RightBox>
          {hasAccess ? (
            <>
              {(courseStatus === LoadingStatuses.Loading ||
                courseStatus === LoadingStatuses.Idle ||
                videoStatus === LoadingStatuses.Idle ||
                videoStatus === LoadingStatuses.Loading) &&
              !videoEnded ? (
                <Loading />
              ) : (
                <>
                  {!videoEnded && (
                    <>
                      <IconsBox>
                        <img src={division?.icon} alt={division?.name} />
                        <AssetTitle style={{marginTop: 0}}>
                          {video?.title}
                        </AssetTitle>
                      </IconsBox>
                      <ObjectiveText>
                        {lesson && <>Lecturer: {lesson?.author}</>}
                      </ObjectiveText>
                      {video?.duration && (
                        <TimeBox>
                          <img src={ClockIcon} alt="Duration" />
                          {video.duration}
                        </TimeBox>
                      )}
                    </>
                  )}

                  <PlayerBox>
                    {videoEnded ? (
                      <VideoEnded
                        videoId={videoId}
                        currentGroup={currentGroup!}
                        nextGroup={nextGroup}
                        currentIndex={currentIndex!}
                        onChangeVideoEnded={onChangeVideoEnded}
                      />
                    ) : (
                      <ReactPlayerLoader
                        ref={playerRef}
                        id={'player'}
                        accountId="5454530067001"
                        playerId={process.env.REACT_APP_BRIGHTCOVE_ID}
                        videoId={video?.brightcoveId}
                        onSuccess={onSuccess}
                        options={{
                          responsive: true,
                          autoplay: cookies.get('bks-autoplay')
                            ? cookies.get('bks-autoplay')
                            : false,
                          muted: false,
                          aspectRatio: '4:3',
                          playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
                        }}
                      />
                    )}
                  </PlayerBox>

                  {video?.transcriptFormatted ? (
                    <TranscriptBox>
                      <TranscriptTitle>Full Transcript</TranscriptTitle>
                      <TranscriptText
                        id="transcript"
                        onContextMenu={e => e.preventDefault()}
                      >
                        {renderHTML(video?.transcriptFormatted)}
                      </TranscriptText>
                    </TranscriptBox>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <>
              <IconsBox>
                <img src={division?.icon} alt={division?.name} />
                <AssetTitle style={{marginTop: 0}}>{video?.title}</AssetTitle>
              </IconsBox>
              <TimeBox>
                <img src={ClockIcon} alt="Duration" />
                {video?.duration}
              </TimeBox>
              <LoggedOutBox>
                <span>You have reached one of our paid content.</span>
                {!hasAccess && auth.isAuthenticated ? (
                  <span>
                    <a
                      href="#"
                      onClick={handleUpgrade(
                        dispatch,
                        auth,
                        member,
                        'videos paywall',
                      )}
                    >
                      <LockIcon /> Upgrade your membership
                    </a>{' '}
                    to get access to this material.
                  </span>
                ) : (
                  <span>
                    You can either <RouterLink to={'/login'}>log in</RouterLink>{' '}
                    to view it or{' '}
                    <a
                      href="https://www.blackspectacles.com/pricing"
                      target="_blank"
                    >
                      learn more about our memberships
                    </a>
                    .
                  </span>
                )}
              </LoggedOutBox>
            </>
          )}
        </RightBox>
      </AssetsBox>
    </>
  )
}

export default View
